import type { AppendMessage } from '@assistant-ui/react'
import type { QueryClient } from '@tanstack/react-query'

import { buildCreateMessageContent } from '@/components/chat-v2/lib/contentConverters'
import { mergePersistedAndLiveSubagentSnapshots } from '@/components/chat-v2/lib/subagentEvents'
import {
  buildAllMessagesFromV3Sync,
  buildCurrentThreadFromV3Sync,
  buildOverlayMessagesFromV3Sync,
  buildPersistedRunMessagesFromV3Sync,
  collectKnownV3SyncObjectIds,
  createEmptyV3SyncCache,
  decideWhetherToSyncV3Digest,
  getCompactionStatusFromV3Overlay,
  mergeV3SyncCache,
  resolveCurrentModelNameFromV3Sync,
  toActiveRunIdFromV3Digest,
  toChatRunStateFromV3StreamState,
  postV3Sync,
  serializeV3SyncFetch,
} from '@/components/chat-v2/lib/v3Sync'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import {
  ChatCompactionStatus,
  ChatMessage,
  ChatRunState,
  OpportunityChatSession,
  OpportunityChatSyncCache,
  OpportunityChatSyncDigest,
  OpportunityChatSyncManifest,
  OpportunityChatSyncOverlayObject,
  OpportunityChatSyncRequest,
  OpportunityChatSyncResponse,
  OpportunityChatSyncRunObject,
  OpportunityChatSyncRunRef,
  Source,
  WithFocusFiles,
  WithOverrideModelName,
  WithSelectedConnectorSources,
  WithSelectedMcpServers,
  WithViewportContext,
  WithWebSearchAnswerTool,
} from '@/types/chat-v2'
import { UUID } from '@/types/Id'

export interface OpportunityChatSyncQueryKeyArgs {
  orgId: UUID
  sessionId: UUID | ''
}

export const opportunityChatSyncQueryKeys = {
  cache: (args: OpportunityChatSyncQueryKeyArgs) =>
    ['chat-session', 'opportunity-sync', args.orgId, args.sessionId] as const,
}

const opportunityChatSessionQueryKey = (
  args: OpportunityChatSyncQueryKeyArgs,
) => ['chat-session', args.orgId, 'opportunity_chat', args.sessionId] as const

export interface OpportunityChatSyncResult {
  didSync: boolean
  reason: string
  session: OpportunityChatSession | null
  runState: ChatRunState
  activeRunId: UUID | null
  compactionStatus: ChatCompactionStatus
}

export type OpportunityChatCreateMessageRequest = WithOverrideModelName &
  WithWebSearchAnswerTool &
  WithFocusFiles &
  WithViewportContext &
  WithSelectedMcpServers &
  WithSelectedConnectorSources & {
    event_type: 'msg:create'
    content: string | Awaited<ReturnType<typeof buildCreateMessageContent>>
    zed_token?: string
  }

export async function sendOpportunityChatCreateMessage(
  args: {
    sessionId: UUID
    message: string | Pick<AppendMessage, 'content'>
    messageParams: Omit<
      OpportunityChatCreateMessageRequest,
      'event_type' | 'content'
    >
  },
  accessToken: string,
): Promise<void> {
  const request: OpportunityChatCreateMessageRequest = {
    event_type: 'msg:create',
    content: await buildCreateMessageContent(args.message),
    ...args.messageParams,
  }

  const url = `${API_URL}/api/v1/chat/opportunity/sessions/${args.sessionId}/protocol/v3/messages`
  const res: Response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(request),
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: {
        status: res.status,
      },
    })
  }
}

export function createEmptyOpportunityChatSyncCache(): OpportunityChatSyncCache {
  return createEmptyV3SyncCache<
    OpportunityChatSyncDigest,
    OpportunityChatSyncManifest,
    OpportunityChatSyncResponse['meta_object'] extends infer T
      ? Exclude<T, null | undefined>
      : never,
    OpportunityChatSyncResponse['view_object'] extends infer T
      ? Exclude<T, null | undefined>
      : never,
    OpportunityChatSyncRunRef,
    OpportunityChatSyncRunObject,
    OpportunityChatSyncOverlayObject,
    OpportunityChatSyncOverlayObject['messages'][number],
    OpportunityChatSyncResponse['subagent_snapshot_objects'][number]
  >()
}

export function fetchOpportunityChatSessionSync(
  queryClient: QueryClient,
  args: OpportunityChatSyncQueryKeyArgs,
  accessToken: string,
  source: string = 'manual',
): Promise<OpportunityChatSession> {
  return serializeV3SyncFetch('opportunity_chat', args.sessionId, () =>
    fetchOpportunityChatSessionSyncNow(queryClient, args, accessToken, source),
  )
}

async function fetchOpportunityChatSessionSyncNow(
  queryClient: QueryClient,
  args: OpportunityChatSyncQueryKeyArgs,
  accessToken: string,
  source: string,
): Promise<OpportunityChatSession> {
  void source
  if (!args.sessionId) {
    throw new Error('Session ID is required')
  }

  const existingCache =
    queryClient.getQueryData<OpportunityChatSyncCache>(
      opportunityChatSyncQueryKeys.cache(args),
    ) ?? createEmptyOpportunityChatSyncCache()

  const req: OpportunityChatSyncRequest = {
    protocol_version: 1,
    known_object_ids: collectKnownV3SyncObjectIds(existingCache),
  }

  const url = `${API_URL}/api/v1/chat/opportunity/sessions/${args.sessionId}/protocol/v3/sync`
  const res = await postV3Sync(url, req, accessToken)

  const syncResponse = (await res.json()) as OpportunityChatSyncResponse
  if (syncResponse.digest.session_id !== args.sessionId) {
    throw new Error(
      `Opportunity chat sync session mismatch: expected ${args.sessionId}, got ${syncResponse.digest.session_id}`,
    )
  }

  const nextCache = mergeOpportunityChatSyncCache(existingCache, syncResponse)
  const session = buildOpportunityChatSessionFromSyncCache(nextCache)

  queryClient.setQueryData(opportunityChatSyncQueryKeys.cache(args), nextCache)
  queryClient.setQueryData(opportunityChatSessionQueryKey(args), session)
  return session
}

export function mergeOpportunityChatSources(
  ...sourceLists: ReadonlyArray<ReadonlyArray<Source> | null | undefined>
): Source[] {
  const sourcesById = new Map<number, Source>()

  for (const sourceList of sourceLists) {
    if (!sourceList) continue

    for (const source of sourceList) {
      sourcesById.set(source.id, source)
    }
  }

  return [...sourcesById.values()]
}

export async function syncOpportunityChatSessionIfNeeded(
  queryClient: QueryClient,
  args: OpportunityChatSyncQueryKeyArgs,
  accessToken: string,
  digest: OpportunityChatSyncDigest,
  source: string,
): Promise<OpportunityChatSyncResult> {
  const cache = queryClient.getQueryData<OpportunityChatSyncCache>(
    opportunityChatSyncQueryKeys.cache(args),
  )
  const decision = decideWhetherToSyncV3Digest(cache, digest)

  if (!decision.shouldSync) {
    return {
      didSync: false,
      reason: decision.reason,
      session: null,
      runState: toChatRunStateFromV3StreamState(digest.stream_state),
      activeRunId: toActiveRunIdFromV3Digest(digest),
      compactionStatus: getCompactionStatusFromV3Overlay(
        cache?.overlay_objects ?? {},
        digest.overlay_object_id,
      ),
    }
  }

  const session = await fetchOpportunityChatSessionSync(
    queryClient,
    args,
    accessToken,
    `${source}:${decision.reason}`,
  )
  const nextCache = queryClient.getQueryData<OpportunityChatSyncCache>(
    opportunityChatSyncQueryKeys.cache(args),
  )
  const nextDigest = nextCache?.digest ?? digest

  return {
    didSync: true,
    reason: decision.reason,
    session,
    runState: toChatRunStateFromV3StreamState(nextDigest.stream_state),
    activeRunId: toActiveRunIdFromV3Digest(nextDigest),
    compactionStatus: getCompactionStatusFromV3Overlay(
      nextCache?.overlay_objects ?? {},
      nextDigest.overlay_object_id,
    ),
  }
}

function mergeOpportunityChatSyncCache(
  cache: OpportunityChatSyncCache,
  response: OpportunityChatSyncResponse,
): OpportunityChatSyncCache {
  return mergeV3SyncCache(cache, response)
}

export function buildOpportunityChatSessionFromSyncCache(
  cache: OpportunityChatSyncCache,
): OpportunityChatSession {
  if (cache.manifest == null) {
    throw new Error('Opportunity chat sync manifest is unavailable')
  }

  const metaObject = cache.meta_objects[cache.manifest.meta_object_id]
  const viewObject = cache.view_objects[cache.manifest.view_object_id]
  if (!metaObject || !viewObject) {
    throw new Error(
      'Opportunity chat sync cache is missing meta or view objects',
    )
  }

  const runRefById = new Map<UUID, OpportunityChatSyncRunRef>(
    cache.manifest.run_refs.map((runRef) => [runRef.run_id, runRef]),
  )
  const messagesByRunId = new Map<UUID, ChatMessage[]>()

  for (const runRef of cache.manifest.run_refs) {
    const runObject = cache.run_objects[runRef.object_id]
    if (!runObject) {
      throw new Error(`Missing opportunity chat run object ${runRef.object_id}`)
    }
    messagesByRunId.set(
      runRef.run_id,
      buildPersistedRunMessagesFromV3Sync(runRefById, runRef, runObject),
    )
  }

  let overlayObject: OpportunityChatSyncOverlayObject | undefined
  if (cache.manifest.overlay_object_id) {
    overlayObject = cache.overlay_objects[cache.manifest.overlay_object_id]
    if (!overlayObject) {
      throw new Error(
        `Missing opportunity chat overlay object ${cache.manifest.overlay_object_id}`,
      )
    }
    messagesByRunId.set(
      overlayObject.run_id,
      buildOverlayMessagesFromV3Sync(overlayObject),
    )
  }

  const currentRunId =
    overlayObject?.run_id ?? viewObject.persisted_current_run_id
  const sources = mergeOpportunityChatSources(
    viewObject.sources,
    overlayObject?.state.sources,
  )
  const subagentSnapshotsByToolCallId = mergePersistedAndLiveSubagentSnapshots(
    viewObject.subagent_snapshot_object_ids_by_tool_call_id,
    overlayObject?.subagent_snapshot_object_ids_by_tool_call_id,
    cache.subagent_snapshot_objects,
  )

  const sharedUserIds =
    metaObject.shared_user_ids ?? metaObject.shared_with_user_ids ?? []

  return {
    id: metaObject.session_id,
    created_at: metaObject.created_at,
    last_modified: metaObject.last_modified,
    initial_model_name: metaObject.initial_model_name,
    initial_agent_name: metaObject.initial_agent_name,
    initial_agent_version: metaObject.initial_agent_version,
    all_messages: buildAllMessagesFromV3Sync(
      cache.manifest,
      messagesByRunId,
      overlayObject,
    ),
    current_thread: buildCurrentThreadFromV3Sync(
      cache.manifest,
      messagesByRunId,
      overlayObject,
      currentRunId,
    ),
    current_run_id: currentRunId,
    subagent_snapshots_by_tool_call_id: subagentSnapshotsByToolCallId,
    current_model_name: resolveCurrentModelNameFromV3Sync(
      cache.manifest,
      cache.run_objects,
      currentRunId,
      overlayObject,
      viewObject.current_model_name,
    ),
    shared_user_ids: sharedUserIds,
    opportunity_id: metaObject.opportunity_id,
    opportunity_type: metaObject.opportunity_type,
    created_by_user_id: metaObject.created_by_user_id,
    organization_id: metaObject.organization_id,
    title: viewObject.title,
    focus_opportunity_file_ids: viewObject.focus_opportunity_file_ids,
    focus_organization_file_ids: viewObject.focus_organization_file_ids,
    focus_user_file_ids: viewObject.focus_user_file_ids,
    sources,
    current_followups: viewObject.current_followups,
    stream_state: cache.digest?.stream_state ?? 'idle',
  }
}
